import { StandardCardProps } from 'features/rewards/components/Rewards.types';
import standardCardStrings from 'features/rewards/components/StandardCard/standardCardStrings.en';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import { Tag, TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export const StandardCard = ({
  primaryText,
  secondaryText,
  isNew,
  logoImage,
  incentiveText,
  backgroundColor = theme.color.background.tertiary,
  isDisabled = false,
  onPress,
  analyticsId = '',
  customAttributes = null,
}: StandardCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const { tag } = standardCardStrings;

  return (
    <PressableWithDelay
      style={[
        styles.cardContainer,
        isFocused ? styles.focus : null,
        isHovered ? styles.hover : null,
        isPressed ? styles.press : null,
      ]}
      onPress={() => {
        cardPressAnalytics();
        onPress?.();
        if (isWeb) {
          setIsFocused(false);
        }
      }}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      role="button"
    >
      <View style={styles.mainView} testID={primaryText}>
        <View style={[styles.cardView, { backgroundColor: backgroundColor }]}>
          {isNew ? (
            <View style={styles.newTagStyle}>
              <Tag text={tag.title} type={TagType.SUCCESS} />
            </View>
          ) : null}
          {incentiveText ? (
            <View style={styles.incentiveTextStyle}>
              <Tag text={incentiveText} type={TagType.INFO_REVERSE} />
            </View>
          ) : null}
        </View>
        <View style={styles.logoImageStyle}>
          <IconAvatar imageSource={logoImage} variant="MD" />
        </View>
        <View style={styles.textView}>
          <BodyBaseText
            numberOfLines={2}
            style={isDisabled ? styles.disabledText : null}
          >
            {primaryText}
          </BodyBaseText>
          {secondaryText ? (
            <BodyXSText
              numberOfLines={2}
              style={isDisabled ? styles.disabledText : null}
            >
              {secondaryText}
            </BodyXSText>
          ) : null}
        </View>
      </View>
    </PressableWithDelay>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.interactive.secondary,
    borderRadius: 4,
    maxHeight: 200,
    width: 160,
  },
  cardView: {
    borderRadius: 4,
    height: 104,
    width: 152,
  },
  disabledText: {
    color: theme.color.interactive.disabled.text,
  },
  focus: {
    outlineColor: theme.color.interactive.focused.border,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  incentiveTextStyle: {
    bottom: theme.spacing.s,
    left: theme.spacing.s,
    position: 'absolute',
  },
  logoImageStyle: {
    paddingHorizontal: 56,
    paddingVertical: theme.spacing.xl,
    position: 'absolute',
  },
  mainView: {
    gap: 8,
    margin: theme.spacing.xs,
  },
  newTagStyle: {
    position: 'absolute',
    right: theme.spacing.s,
    top: theme.spacing.s,
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  textView: {
    gap: 4,
  },
});

export default StandardCard;
